import { uid } from '@/utils';
import { getDb, getLegacyDb, getUserDb } from './db';
import { saveUIState } from './ui-state';
export { runMigrations, getDbmVersion } from './migrations';
export { setCurrentUserId } from './db';
// --- User-scoped operations (use getUserDb via getDb) ---
export async function loadBudgets() {
    const db = await getDb();
    return db.getAll('budgets');
}
export async function saveBudgets(budgets) {
    const db = await getDb();
    const tx = db.transaction('budgets', 'readwrite');
    const store = tx.objectStore('budgets');
    const existingKeys = await store.getAllKeys();
    const newIds = new Set(budgets.map((b) => b.id));
    for (const key of existingKeys) {
        if (!newIds.has(key))
            await store.delete(key);
    }
    for (const b of budgets) {
        await store.put(b);
    }
    await tx.done;
}
export async function loadProfile() {
    const db = await getDb();
    const row = await db.get('meta', 'profile');
    return row ? row.data : null;
}
export async function saveProfile(profile) {
    const db = await getDb();
    await db.put('meta', { key: 'profile', data: profile });
}
export async function clearAllBudgets() {
    const db = await getDb();
    await db.clear('budgets');
}
// --- Meta helpers for auth (user-scoped) ---
export async function loadDeviceId() {
    // deviceId is shared across users — stored in legacy DB
    const db = await getLegacyDb();
    const row = await db.get('meta', 'deviceId');
    return row ? row.data : null;
}
export async function saveDeviceId(id) {
    const db = await getLegacyDb();
    await db.put('meta', { key: 'deviceId', data: id });
}
// --- Legacy DB operations (pre-auth data) ---
export async function loadLegacyBudgets() {
    const db = await getLegacyDb();
    return db.getAll('budgets');
}
export async function clearLegacyBudgets() {
    const db = await getLegacyDb();
    await db.clear('budgets');
}
export async function loadLegacyProfile() {
    const db = await getLegacyDb();
    const row = await db.get('meta', 'profile');
    return row ? row.data : null;
}
/** Move selected budgets from legacy DB to the current user's DB, delete others */
export async function applyLegacyDecisions(decisions) {
    const legacyDb = await getLegacyDb();
    const userDb = await getUserDb();
    const keepIds = new Set(decisions.filter((d) => d.action === 'keep').map((d) => d.budgetId));
    const deleteIds = new Set(decisions.filter((d) => d.action === 'delete').map((d) => d.budgetId));
    const budgets = await legacyDb.getAll('budgets');
    // Move "keep" budgets to user DB
    if (keepIds.size > 0) {
        const tx = userDb.transaction('budgets', 'readwrite');
        const store = tx.objectStore('budgets');
        for (const b of budgets) {
            if (keepIds.has(b.id)) {
                await store.put(b);
            }
        }
        await tx.done;
    }
    // Move profile if any budgets are kept
    if (keepIds.size > 0) {
        const profileRow = await legacyDb.get('meta', 'profile');
        if (profileRow) {
            const existingProfile = await userDb.get('meta', 'profile');
            if (!existingProfile) {
                await userDb.put('meta', { key: 'profile', data: profileRow.data });
            }
        }
    }
    // Remove "keep" and "delete" budgets from legacy DB; "defer" stays
    const removeIds = new Set([...keepIds, ...deleteIds]);
    if (removeIds.size > 0) {
        const tx = legacyDb.transaction('budgets', 'readwrite');
        const store = tx.objectStore('budgets');
        for (const id of removeIds) {
            await store.delete(id);
        }
        await tx.done;
    }
    // If legacy DB is now empty, clean up profile too
    const remaining = await legacyDb.count('budgets');
    if (remaining === 0) {
        await legacyDb.delete('meta', 'profile');
    }
}
/** Check if legacy DB has any budgets (pre-auth data) */
export async function hasLegacyBudgets() {
    const db = await getLegacyDb();
    const count = await db.count('budgets');
    return count > 0;
}
// --- localStorage migration (very old versions) ---
export async function migrateFromLocalStorage() {
    const raw = localStorage.getItem('budgetapp4');
    if (!raw)
        return null;
    try {
        const parsed = JSON.parse(raw);
        const budgets = parsed.budgets ?? [];
        const rawProfile = parsed.profile ?? { name: 'Пользователь', avatar: '🙂' };
        const profile = { id: rawProfile.id ?? uid(), ...rawProfile };
        const ui = {
            year: parsed.year ?? new Date().getFullYear(),
            month: parsed.month ?? new Date().getMonth(),
            selectedDate: parsed.selectedDate ?? null,
            calMode: parsed.calMode ?? 'month',
            view: parsed.view ?? 'home',
            budgetView: parsed.budgetView ?? 'calendar',
            currentId: parsed.currentId ?? null,
        };
        // Migrate to legacy DB (will be moved to user DB on login)
        const db = await getLegacyDb();
        const tx = db.transaction('budgets', 'readwrite');
        const store = tx.objectStore('budgets');
        for (const b of budgets) {
            await store.put(b);
        }
        await tx.done;
        await db.put('meta', { key: 'profile', data: profile });
        saveUIState(ui);
        localStorage.removeItem('budgetapp4');
        return { budgets, profile, ui };
    }
    catch {
        return null;
    }
}
